import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Search,
  BookOpen,
  Clock,
  Star,
  Play,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  GraduationCap,
  Award,
  Brain,
  Code,
  Palette,
  Database,
  ShieldCheck,
  Cpu,
  Volume2,
  Maximize,
  PlayCircle,
  Library,
  Sparkles,
  Trophy,
  ChevronDown,
  Signal,
  Zap,
} from "lucide-react";
import { getAllCourses } from "../../network/APIs/industry-courses";

// --- DATA STRUCTURES ---
/*
// TypeScript interfaces (commented out for JavaScript)
export interface Lesson {
  id: string;
  title: string;
  duration: string;
  completed: boolean;
}

export interface Course {
  id: string;
  title: string;
  category: 'Development' | 'AI / ML' | 'Design' | 'DevOps / Cloud' | 'Systems';
  summary: string;
  description: string;
  duration: string;
  lessonsCount: number;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  rating: number;
  enrolledStudentsCount: string;
  trainer: {
    name: string;
    role: string;
    avatar: string;
  };
  syllabus: Lesson[];
}
*/

// --- MOCK COURSES DATA (for reference only, not used) ---
/*
const MOCK_COURSES = [
  {
    id: 'course-1',
    title: 'AI-Ready Full Stack Developer (Next.js & Gemini AI)',
    category: 'AI / ML',
    summary: 'Master full stack development with serverless routes, React Server Components, and live Google GenAI SDK integrations.',
    description: 'Bridge the gap between traditional full-stack web applications and artificial intelligence. Learn to construct edge API routes, optimize real-time streaming tokens, govern server states, and securely manage Gemini API endpoints.',
    duration: '6 Weeks',
    lessonsCount: 6,
    difficulty: 'Intermediate',
    rating: 4.9,
    enrolledStudentsCount: '12.4K',
    trainer: {
      name: 'Dr. Aris Vance',
      role: 'Principal AI Architect',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '1-1', title: 'Next.js App Router & Server Components', duration: '45 mins', completed: false },
      { id: '1-2', title: 'Securing API Routes and Client Proxies', duration: '35 mins', completed: false },
      { id: '1-3', title: 'Integrating @google/genai SDK on Server-Side', duration: '50 mins', completed: false },
      { id: '1-4', title: 'Handling Markdown & Streaming Responses in real time', duration: '40 mins', completed: false },
      { id: '1-5', title: 'Caching & Session State with Vercel KV', duration: '55 mins', completed: false },
      { id: '1-6', title: 'Caponstone Project: Designing an AI Copilot Interface', duration: '120 mins', completed: false }
    ]
  },
  {
    id: 'course-2',
    title: 'Advanced System Design & Scalable Architectures',
    category: 'Systems',
    summary: 'Master trade-offs in horizontal scaling, Redis caching tiers, message brokers, and highly durable database isolation.',
    description: 'Learn the architectural strategies used by elite engineering teams to manage billions of requests. Understand consistency choices, load balancing algorithms, database sharding, caching strategies, and event sourcing pipelines.',
    duration: '8 Weeks',
    lessonsCount: 5,
    difficulty: 'Advanced',
    rating: 4.8,
    enrolledStudentsCount: '8.1K',
    trainer: {
      name: 'Rohan Deshmukh',
      role: 'Staff Infrastructure Engineer',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '2-1', title: 'Horizontal vs Vertical Scaling Mechanics', duration: '50 mins', completed: false },
      { id: '2-2', title: 'In-Memory Cache Strategy with Redis Clustered Keys', duration: '45 mins', completed: false },
      { id: '2-3', title: 'Decoupling services with Apache Kafka Message Queues', duration: '60 mins', completed: false },
      { id: '2-4', title: 'Database Replication, Sharding, & ACID Consistency', duration: '55 mins', completed: false },
      { id: '2-5', title: 'SLA design: CDNs, Rate Limiters, & Edge Routing', duration: '40 mins', completed: false }
    ]
  },
  {
    id: 'course-3',
    title: 'Product Design, Interaction Principles & UI/UX',
    category: 'Design',
    summary: 'Design crisp visual hierarchies, intuitive interaction maps, custom style guidelines, and perform structured user test evaluations.',
    description: 'Craft beautiful digital interfaces that balance form, function, and seamless ergonomics. Learn typography scaling systems, responsive layout foundations, accessibility guidelines (WCAG), and dynamic micro-animations.',
    duration: '5 Weeks',
    lessonsCount: 5,
    difficulty: 'Beginner',
    rating: 4.7,
    enrolledStudentsCount: '9.5K',
    trainer: {
      name: 'Elena Rostova',
      role: 'Head of Product UX',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '3-1', title: 'Introduction to User-Centered Design Foundations', duration: '30 mins', completed: false },
      { id: '3-2', title: 'Typography Pairings, Visual Weights, & Grids', duration: '40 mins', completed: false },
      { id: '3-3', title: 'Component States (Hover, Active, Disabled) & Micro-interactions', duration: '35 mins', completed: false },
      { id: '3-4', title: 'Designing for Web Accessibility (WCAG 2.1 AAA)', duration: '45 mins', completed: false },
      { id: '3-5', title: 'Conducting Interactive Heuristic Evaluative Tests', duration: '55 mins', completed: false }
    ]
  },
  {
    id: 'course-4',
    title: 'Cloud-Native DevOps & Kubernetes Containers',
    category: 'DevOps / Cloud',
    summary: 'Dockerize complex web applications, build secure GitHub Actions pipelines, and scale deployments inside state clusters.',
    description: 'Dive deep into automated container orchestration. Learn container networking security, persistent volumes, auto-scaling horizontal pod controllers, and seamless zero-downtime canary rollouts on cloud infrastructure.',
    duration: '7 Weeks',
    lessonsCount: 6,
    difficulty: 'Advanced',
    rating: 4.9,
    enrolledStudentsCount: '6.7K',
    trainer: {
      name: 'Marcus Sterling',
      role: 'Principal Cloud Advocate',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '4-1', title: 'Docker Internals & Multistage Layer Optimizations', duration: '40 mins', completed: false },
      { id: '4-2', title: 'Slick CI/CD Automation with GitHub Actions & Secrets', duration: '45 mins', completed: false },
      { id: '4-3', title: 'Introduction to Kubernetes pods, services and configs', duration: '50 mins', completed: false },
      { id: '4-4', title: 'Networking Ingress & Load Balancing inside K8s', duration: '55 mins', completed: false },
      { id: '4-5', title: 'Helm Charts for Declarative Resource Mapping', duration: '40 mins', completed: false },
      { id: '4-6', title: 'Production Site Reliability, Metrics, and PromQL', duration: '60 mins', completed: false }
    ]
  },
  {
    id: 'course-5',
    title: 'Data Science & Big Data Engineering with Spark',
    category: 'AI / ML',
    summary: 'Govern massive big data streams, design ML models, and construct PySpark analytical pipelines.',
    description: 'Uncover business and user intelligence locked inside extreme datasets. Clean data at scale, utilize Apache Spark cluster resources, implement linear/logistic models, and design robust dashboards.',
    duration: '8 Weeks',
    lessonsCount: 5,
    difficulty: 'Intermediate',
    rating: 4.6,
    enrolledStudentsCount: '11.2K',
    trainer: {
      name: 'Nisha Pillai',
      role: 'Staff Data Scientist',
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '5-1', title: 'Fundamental exploratory data cleaning with Pandas', duration: '40 mins', completed: false },
      { id: '5-2', title: 'Introduction to Spark Context & Cluster Ingestion', duration: '50 mins', completed: false },
      { id: '5-3', title: 'Creating PySpark Pipelines for Large Datasets', duration: '55 mins', completed: false },
      { id: '5-4', title: 'Deploying Supervised Classification Models', duration: '45 mins', completed: false },
      { id: '5-5', title: 'Big Data Visuals, Metrics, and D3.js Charts', duration: '60 mins', completed: false }
    ]
  },
  {
    id: 'course-6',
    title: 'Universal Typescript & High-Performance Node.js APIs',
    category: 'Development',
    summary: 'Build concurrent server applications, write typed middleware solutions, and optimize database queries.',
    description: 'Architect Node.js services with robust TypeScript typing. Address threading, optimize event loops, manage high concurrent connections, prevent injection vulnerabilities, and construct SQL query indexes.',
    duration: '6 Weeks',
    lessonsCount: 5,
    difficulty: 'Intermediate',
    rating: 4.8,
    enrolledStudentsCount: '15.3K',
    trainer: {
      name: 'Gavin Miller',
      role: 'Senior Backend Developer',
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '6-1', title: 'Deep Dive: Event Loop & Task Queues in Node.js', duration: '45 mins', completed: false },
      { id: '6-2', title: 'Type-Safe Express Server Framework Setup', duration: '35 mins', completed: false },
      { id: '6-3', title: 'Advanced Type Guards, Enums, & Generic APIs', duration: '55 mins', completed: false },
      { id: '6-4', title: 'ORM Integration & Query Execution optimization', duration: '50 mins', completed: false },
      { id: '6-5', title: 'Stress Testing: Profiling Memory Leaks', duration: '40 mins', completed: false }
    ]
  },
  {
    id: 'course-7',
    title: 'Fintech Blockchains & Web3 Security Protocols',
    category: 'Systems',
    summary: 'Construct distributed networks, program secure Solidity contracts, and deploy banking validation rules.',
    description: 'Master decentralised trust infrastructures. Program, test, and deploy smart contract applications. Audit state security against common exploits like reentrancy, frontrunning, and overflows.',
    duration: '8 Weeks',
    lessonsCount: 5,
    difficulty: 'Advanced',
    rating: 4.7,
    enrolledStudentsCount: '5.2K',
    trainer: {
      name: 'Satoshi Nakamori',
      role: 'Cryptography Pioneer',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '7-1', title: 'Cryptographic Hashing & Consolidated Ledger Consensus', duration: '40 mins', completed: false },
      { id: '7-2', title: 'Solidity Structs, Mapping, & Contract Methods', duration: '50 mins', completed: false },
      { id: '7-3', title: 'Preventing Reentrancy & Safe ERC20 Standards', duration: '55 mins', completed: false },
      { id: '7-4', title: 'Implementing Off-Chain Data feeds with Oracles', duration: '45 mins', completed: false },
      { id: '7-5', title: 'End-to-End Decentralized App dApp Audits', duration: '60 mins', completed: false }
    ]
  },
  {
    id: 'course-8',
    title: 'Mobile App Architecture with Flutter & State Hooks',
    category: 'Development',
    summary: 'Build highly responsive iOS & Android user interfaces using reactive routing, state blocks, and offline local cache.',
    description: 'Learn modern mobile architecture by design. Bridge platform hardware capabilities, build smooth 120Hz scrolling animations, configure offline databases, and bundle production outputs.',
    duration: '5 Weeks',
    lessonsCount: 5,
    difficulty: 'Beginner',
    rating: 4.6,
    enrolledStudentsCount: '10.8K',
    trainer: {
      name: 'Jessica Wood',
      role: 'Mobile Team Lead',
      avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=128'
    },
    syllabus: [
      { id: '8-1', title: 'Dart Fundamentals & Flutter Widget Lifecycles', duration: '45 mins', completed: false },
      { id: '8-2', title: 'Declarative State Management Patterns', duration: '40 mins', completed: false },
      { id: '8-3', title: 'Connecting Native Device Drivers & Storage', duration: '50 mins', completed: false },
      { id: '8-4', title: 'Offline-First SQLite Cache Configurations', duration: '45 mins', completed: false },
      { id: '8-5', title: 'Slick Fluid Animations at 120 FPS Rendering', duration: '55 mins', completed: false }
    ]
  }
];
*/

const ITEMS_PER_PAGE = 8;

export default function IndustryCoursesPage() {
  // Search and Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDifficulty, setSelectedDifficulty] = useState("");
  const [isDifficultyDropdownOpen, setIsDifficultyDropdownOpen] =
    useState(false);
  const [hoveredDifficulty, setHoveredDifficulty] = useState(null);
  const difficultyDropdownRef = useRef(null);

  // 'All' | 'Development' | 'AI / ML' | 'Design' | 'DevOps / Cloud' | 'Systems
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // API state
  const [courses, setCourses] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Learning Portal states
  const [activeCourse, setActiveCourse] = useState(null);
  const [userLessonsState, setUserLessonsState] = useState({});
  const [playbackState, setPlaybackState] = useState({
    isPlaying: false,
    progress: 12,
    volume: 80,
  });
  const [showCertificateConfetti, setShowCertificateConfetti] = useState(false);

  // Fetch courses from API
  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        const response = await getAllCourses({
          search: searchQuery,
          difficulty: selectedDifficulty,
          offset,
          limit: ITEMS_PER_PAGE,
        });
        console.log("course", response);
        setCourses(response.data || response || []);
        setTotalRecords(response.totalRecords || 0);
      } catch (err) {
        setError("Failed to load courses. Please try again.");
        console.error("Error fetching courses:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, [searchQuery, selectedDifficulty, currentPage]);

  // Filter calculations (only category filtering, search is handled by API)
  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const matchCategory =
        selectedCategory === "All" || course.category === selectedCategory;
      return matchCategory;
    });
  }, [courses, selectedCategory]);

  // Reset page when filters change (resets offset to 0)
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedDifficulty]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        difficultyDropdownRef.current &&
        !difficultyDropdownRef.current.contains(event.target)
      ) {
        setIsDifficultyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(totalRecords / ITEMS_PER_PAGE));
  const currentCourses = courses;
  const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const indexOfLastItem = Math.min(currentPage * ITEMS_PER_PAGE, totalRecords);
  const totalItems = totalRecords;

  // Lesson status toggle
  const toggleLesson = (lessonId) => {
    setUserLessonsState((prev) => {
      const updated = { ...prev, [lessonId]: !prev[lessonId] };
      if (activeCourse) {
        const courseSyllabus = activeCourse.syllabus;
        const allCompleted = courseSyllabus.every(
          (lesson) => updated[lesson.id] === true,
        );
        if (allCompleted) {
          setShowCertificateConfetti(true);
        } else {
          setShowCertificateConfetti(false);
        }
      }
      return updated;
    });
  };

  // Get active course completion metrics
  const getCourseProgress = (course) => {
    const syllabus = course.syllabus || [];
    const total = syllabus.length;
    let completedCount = 0;
    syllabus.forEach((lesson) => {
      if (userLessonsState[lesson.id]) completedCount++;
    });
    const percentage =
      total > 0 ? Math.round((completedCount / total) * 100) : 0;
    return { completedCount, total, percentage };
  };

  // Simulated Certificate Download
  const triggerCertificatePdf = (courseTitle) => {
    alert(
      `🏆 Congratulations! Your validated 'Talent Connect Industry Certificate' for and under "${courseTitle}" has been generated and ready to download.\n\nCredential ID: TC-CERT-${Math.floor(100000 + Math.random() * 900000)}`,
    );
  };

  // Categorised Course Counters for pills
  const categoryCounts = useMemo(() => {
    const counts = { All: courses.length };
    courses.forEach((course) => {
      counts[course.category] = (counts[course.category] || 0) + 1;
    });
    return counts;
  }, [courses]);

  return (
    <>
      <style>{`
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-display: swap;
          src: url(https://fonts.gstatic.com/s/inter/v20/UcC73FwrK3iLTeHuS_nVMrMxCp50SjIa2JL7SUc.woff2) format('woff2');
          unicode-range: U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8 mt-16">
        <div className="space-y-8 animate-in fade-in duration-300">
          {/* 1. Header & Quick Stat Widget */}

          <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white rounded-[2rem] p-8 sm:p-6 border border-indigo-900/30 relative overflow-hidden shadow-xl">
            <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/10 rounded-full blur-[120px] -mr-40 -mt-20 pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-teal-500/5 rounded-full blur-[80px] pointer-events-none"></div>

            <div className="relative z-10 grid grid-cols-12 sm:grid-cols-1 gap-6 items-center">
              <div className="col-span-12 sm:col-span-full space-y-3 text-left font-sans">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-500/20 border border-brand-500/30 rounded-full text-xs font-bold text-brand-300">
                  <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                  Startup-Validated Syllabus
                </span>
                <h2 className="text-3.5xl sm:text-2xl font-extrabold tracking-tight leading-tight">
                  Launch Your Career with Industry-Endorsed Credentials
                </h2>
                <p className="text-slate-350 text-base sm:text-sm leading-relaxed max-w-3xl font-medium">
                  Curated by founders, staff engineers, and lead designers.
                  Upgrade your technical and leadership metrics to instantly
                  unlock interview Fast-Tracks on Talent Connect.
                </p>
              </div>
            </div>
          </div>

          {/* 2. Search & Categories Navigation Bar */}

          <div className="flex flex-col gap-4 bg-white p-5 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.015)]">
            <div className="flex flex-row  gap-4 items-center xsm:flex-wrap">
              {/* Search Input */}
              <div className=" relative font-sans flex-1">
                <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search via keyword, technology, course title..."
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all shadow-inner"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3.5 top-3.5 text-xs font-bold text-slate-400 hover:text-slate-600 transition"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Difficulty Dropdown */}
              <div
                className="font-sans sm:w-auto relative"
                ref={difficultyDropdownRef}
              >
                <button
                  onClick={() =>
                    setIsDifficultyDropdownOpen(!isDifficultyDropdownOpen)
                  }
                  className="w-full sm:w-48 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all shadow-sm hover:shadow-lg hover:border-brand-400 hover:bg-slate-50 flex items-center justify-between gap-2"
                >
                  <span>{selectedDifficulty || "All Levels"}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isDifficultyDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isDifficultyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setSelectedDifficulty("");
                          setIsDifficultyDropdownOpen(false);
                        }}
                        onMouseEnter={() => setHoveredDifficulty("")}
                        onMouseLeave={() => setHoveredDifficulty(null)}
                        style={
                          hoveredDifficulty === "" ||
                          (selectedDifficulty === "" &&
                            hoveredDifficulty === null)
                            ? { backgroundColor: "rgb(212 231 255 / 90%)" }
                            : {}
                        }
                        className={`w-full px-4 py-3 text-sm font-semibold text-left transition-all ${hoveredDifficulty === "" ? "border-l-4 border-blue-600" : selectedDifficulty === "" && hoveredDifficulty === null ? "text-brand-600 border-l-4 border-blue-600" : "text-slate-700 border-l-4 border-transparent"}`}
                      >
                        All Levels
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDifficulty("Beginner");
                          setIsDifficultyDropdownOpen(false);
                        }}
                        onMouseEnter={() => setHoveredDifficulty("Beginner")}
                        onMouseLeave={() => setHoveredDifficulty(null)}
                        style={
                          hoveredDifficulty === "Beginner" ||
                          (selectedDifficulty === "Beginner" &&
                            hoveredDifficulty === null)
                            ? { backgroundColor: "rgb(212 231 255 / 90%)" }
                            : {}
                        }
                        className={`w-full px-4 py-3 text-sm font-semibold text-left transition-all ${hoveredDifficulty === "Beginner" ? "border-l-4 border-blue-600" : selectedDifficulty === "Beginner" && hoveredDifficulty === null ? "text-brand-600 border-l-4 border-blue-600" : "text-slate-700 border-l-4 border-transparent"}`}
                      >
                        Beginner
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDifficulty("Intermediate");
                          setIsDifficultyDropdownOpen(false);
                        }}
                        onMouseEnter={() =>
                          setHoveredDifficulty("Intermediate")
                        }
                        onMouseLeave={() => setHoveredDifficulty(null)}
                        style={
                          hoveredDifficulty === "Intermediate" ||
                          (selectedDifficulty === "Intermediate" &&
                            hoveredDifficulty === null)
                            ? { backgroundColor: "rgb(212 231 255 / 90%)" }
                            : {}
                        }
                        className={`w-full px-4 py-3 text-sm font-semibold text-left transition-all ${hoveredDifficulty === "Intermediate" ? "border-l-4 border-blue-600" : selectedDifficulty === "Intermediate" && hoveredDifficulty === null ? "text-brand-600 border-l-4 border-blue-600" : "text-slate-700 border-l-4 border-transparent"}`}
                      >
                        Intermediate
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDifficulty("Advanced");
                          setIsDifficultyDropdownOpen(false);
                        }}
                        onMouseEnter={() => setHoveredDifficulty("Advanced")}
                        onMouseLeave={() => setHoveredDifficulty(null)}
                        style={
                          hoveredDifficulty === "Advanced" ||
                          (selectedDifficulty === "Advanced" &&
                            hoveredDifficulty === null)
                            ? { backgroundColor: "rgb(212 231 255 / 90%)" }
                            : {}
                        }
                        className={`w-full px-4 py-3 text-sm font-semibold text-left transition-all ${hoveredDifficulty === "Advanced" ? "border-l-4 border-blue-600" : selectedDifficulty === "Advanced" && hoveredDifficulty === null ? "text-brand-600 border-l-4 border-blue-600" : "text-slate-700 border-l-4 border-transparent"}`}
                      >
                        Advanced
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedDifficulty("");
                  setSelectedCategory("All");
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-slate-100 w-[150px] hover:bg-slate-200 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 transition-all"
                title="Reset Filters"
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* 3. Paginated Course Grid */}
          {isLoading ? (
            <div className="bg-white border border-slate-100 rounded-[2rem] p-12 text-center shadow-sm">
              <div className="w-16 h-16 border-4 border-slate-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">
                Loading courses...
              </h3>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-100 rounded-[2rem] p-12 text-center shadow-sm">
              <BookOpen className="w-16 h-16 text-rose-300 mx-auto mb-4" />
              <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">
                Error loading courses
              </h3>
              <p className="text-slate-450 text-sm mt-1 max-w-sm mx-auto font-semibold leading-relaxed">
                {error}
              </p>
              <button
                onClick={() => {
                  setError(null);
                }}
                className="mt-6 bg-brand-600 hover:bg-brand-700 text-white text-xs font-black px-5 py-3 rounded-xl transition"
              >
                Retry
              </button>
            </div>
          ) : filteredCourses.length > 0 ? (
            <div className="space-y-8">
              <div className="grid grid-cols-2 xsm:grid-cols-1 gap-6">
                {currentCourses.map((course, index) => {
                  const progress = getCourseProgress(course);
                  const isEnrolled = progress.completedCount > 0;
                  const isFullyFinished = progress.percentage === 100;

                  // Gradient colors based on index (0-3)
                  const gradientColors = [
                    "from-purple-500 to-indigo-500",
                    "from-orange-500 to-amber-500",
                    "from-pink-500 to-rose-500",
                    "from-blue-500 to-cyan-500",
                  ];
                  const gradientClass =
                    gradientColors[index % gradientColors.length];

                  // Map dynamic icons
                  const CategoryIcon = () => {
                    switch (course.category) {
                      case "AI/ML":
                        return <Brain className="w-4 h-4 text-purple-600" />;
                      case "Development":
                        return <Code className="w-4 h-4 text-emerald-600" />;
                      case "Web Design":
                        return <Palette className="w-4 h-4 text-pink-600" />;
                      case "DevOps / Cloud":
                        return <Cpu className="w-4 h-4 text-blue-600" />;
                      case "System":
                        return <Database className="w-4 h-4 text-orange-600" />;
                      default:
                        return <BookOpen className="w-4 h-4 text-slate-600" />;
                    }
                  };

                  return (
                    <div
                      key={course.id || course.course_slug}
                      className="bg-white rounded-3xl border border-slate-100/80 shadow-[0_4px_20px_rgba(0,0,0,0.015)] overflow-hidden flex flex-col hover:shadow-xl hover:border-slate-200 transition-all duration-300 group"
                    >
                      {/* Top colored container bar */}
                      <div
                        className={`h-2.5 bg-gradient-to-r ${gradientClass}`}
                      ></div>

                      <div className="p-6 flex-1 flex flex-col justify-between">
                        <div>
                          {/* Meta Tags */}
                          <div className="flex items-center justify-between mb-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider ${
                                course.category === "AI/ML"
                                  ? "bg-purple-50 text-purple-700"
                                  : course.category === "Development"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : course.category === "Web Design"
                                      ? "bg-pink-50 text-pink-700"
                                      : course.category === "System"
                                        ? "bg-blue-50 text-orange-700"
                                        : "bg-orange-50 text-blue-700"
                              }`}
                            >
                              <CategoryIcon />
                              {course.category}
                            </span>
                            <div className="flex items-center gap-3">
                              {course.difficulty_level && (
                                <span
                                  className={`text-[11px] font-bold px-2 py-1 rounded-md 
                                    ${
                                      course.difficulty_level === "Beginner"
                                        ? "bg-slate-100 text-slate-600"
                                        : course.difficulty_level ===
                                            "Intermediate"
                                          ? "bg-amber-50 text-amber-700"
                                          : course.difficulty_level ===
                                              "Advanced"
                                            ? "bg-amber-100 text-red-600"
                                            : "bg-slate-50 text-slate-600"
                                    }`}
                                >
                                  {course.difficulty_level}
                                </span>
                              )}
                              {course.duration && (
                                <span className="flex items-center gap-1 font-bold text-slate-500 text-xs">
                                  <Clock className="w-3.5 h-3.5" />
                                  {course.duration}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Title & Summary */}

                          <h3
                            className="font-extrabold text-slate-900 group-hover:text-brand-600 transition tracking-tight"
                            style={{
                              fontSize: "1.125rem",
                              lineHeight: "1.75rem",
                            }}
                          >
                            {course.course_title}
                          </h3>
                          {course.description && (
                            <p
                              className="mt-2 line-clamp-3"
                              style={{
                                fontSize: "0.950rem",
                                lineHeight: "1.25rem",
                                fontWeight: 500,
                                fontFamily: "Inter, sans-serif",
                                "--tw-text-opacity": 1,
                                color:
                                  "rgb(100 116 139 / var(--tw-text-opacity, 1))",
                              }}
                            >
                              {course.description}
                            </p>
                          )}

                          {/* Tags */}
                          {course.tags && (
                            <div className="flex flex-wrap gap-1.5 mt-3.5">
                              {course.tags
                                .split(",")
                                .slice(0, 5)
                                .map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-500 text-[10px] font-bold rounded-lg transition-colors hover:bg-slate-100"
                                  >
                                    {tag.trim()}
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>

                        <div className="mt-6 pt-5 border-t border-slate-50 space-y-4">
                          {/* Course Progress Bar (If Enrolled/Started) */}
                          {isEnrolled && (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-xs font-bold">
                                <span className="text-slate-500 flex items-center gap-1">
                                  <CheckCircle
                                    className={`w-3.5 h-3.5 ${isFullyFinished ? "text-emerald-500" : "text-brand-500"}`}
                                  />
                                  Course Progress
                                </span>
                                <span
                                  className={
                                    isFullyFinished
                                      ? "text-emerald-600"
                                      : "text-slate-700"
                                  }
                                >
                                  {progress.completedCount}/{progress.total}{" "}
                                  modules ({progress.percentage}%)
                                </span>
                              </div>
                              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${isFullyFinished ? "bg-emerald-500" : "bg-brand-500"}`}
                                  style={{ width: `${progress.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          )}

                          {/* Provider & Action Button */}
                          <div className="flex items-center justify-between gap-3 font-sans">
                            {course.provider_name && (
                              <div className="flex items-center gap-2.5 bg-emerald-500/[0.04] border border-emerald-500/12 rounded-2xl py-2 px-3">
                                <div className="w-7 h-7 rounded-xl bg-emerald-500 flex items-center justify-center text-[11px] text-white font-black tracking-tighter shadow-md shadow-emerald-500/15">
                                  {course.provider_name.charAt(0)}
                                </div>
                                <div className="text-left">
                                  <span className="text-[8px] text-emerald-600 font-extrabold uppercase tracking-widest block leading-none">
                                    PROVIDER
                                  </span>
                                  <span className="text-xs font-black text-slate-900 leading-none block mt-1.5">
                                    {course.provider_name}
                                  </span>
                                </div>
                              </div>
                            )}

                            {(course.embed_url || course.preview_video_url) && (
                              <div className="flex items-center gap-2">
                                <a
                                  href={
                                    course.embed_url || course.preview_video_url
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`cursor-pointer px-5 py-3 rounded-xl text-xs font-extrabold transition-all duration-300 transform active:scale-95 flex items-center gap-2 border shadow-sm ${
                                    isFullyFinished
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100"
                                      : isEnrolled
                                        ? "bg-brand-50 text-brand-700 border-brand-100 hover:bg-brand-100"
                                        : "bg-slate-900 border-slate-900 text-white hover:bg-slate-850"
                                  }`}
                                >
                                  <Play className="w-3.5 h-3.5 fill-current" />
                                  {isFullyFinished
                                    ? "Review Course"
                                    : isEnrolled
                                      ? "Resume Learning"
                                      : "Start Learning"}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 4. Elegant Pagination Bar */}

              <div className="flex flex-row xsm:flex-col justify-between items-center bg-white border border-slate-100 p-5 rounded-3xl gap-4 shadow-[0_4px_20px_rgba(0,0,0,0.01)]">
                <div className="text-xs text-slate-500 font-semibold">
                  Showing{" "}
                  <span className="font-bold text-slate-800">
                    {indexOfFirstItem + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-bold text-slate-800">
                    {Math.min(indexOfLastItem, totalItems)}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-slate-800">{totalItems}</span>{" "}
                  top industry courses
                </div>

                <div className="flex items-center gap-2 xsm:flex-wrap xsm:items-end justify-end">
                  {/* Prev Button */}
                  <button
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    className={`p-2.5 rounded-xl border border-slate-200 transition flex items-center justify-center ${
                      currentPage === 1
                        ? "text-slate-300 bg-slate-50 border-slate-100 cursor-not-allowed"
                        : "text-slate-600 bg-white hover:bg-slate-50"
                    }`}
                    title="Previous Page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const pageNum = index + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`min-w-10 h-10 px-3.5 rounded-xl text-xs font-black transition-all ${
                          currentPage === pageNum
                            ? "bg-slate-900 text-white shadow-md"
                            : "text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Next Button */}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    className={`p-2.5 rounded-xl border border-slate-200 transition flex items-center justify-center ${
                      currentPage === totalPages
                        ? "text-slate-300 bg-slate-50 border-slate-100 cursor-not-allowed"
                        : "text-slate-600 bg-white hover:bg-slate-50"
                    }`}
                    title="Next Page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Empty Search Results */
            <div className="bg-white border border-slate-100 rounded-[2rem] p-12 text-center shadow-sm">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4 animate-bounce" />
              <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">
                No courses found matching "{searchQuery}"
              </h3>
              <p className="text-slate-450 text-sm mt-1 max-w-sm mx-auto font-semibold leading-relaxed">
                Try correcting typos, broadening filter bounds, or choosing
                alternate premium headers.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedDifficulty("");
                  setSelectedCategory("All");
                  setCurrentPage(1);
                }}
                className="mt-6 bg-slate-900 hover:bg-slate-850 text-white text-xs font-black px-5 py-3 rounded-xl transition"
              >
                Reset Filters
              </button>
            </div>
          )}

          {/* 5. Course Learning Overlay Portal (Modal) */}

          {
            // activeCourse && (
            //   <div className="fixed inset-0  z-50 bg-slate-900/90 flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
            //     <div className="w-full sm:mt-48 xsm:mt-36 max-w-5xl lg:max-w-4xl md:max-w-3xl sm:max-w-[94%] mx-auto rounded-[2.5rem] overflow-y-auto overflow-x-hidden border border-slate-200 shadow-2xl flex flex-col grid grid-cols-12 sm:grid-cols-1 max-h-[90vh] min-h-[50vh] relative text-left">
            //       {/* Close Button at top-right */}
            //       <button
            //         onClick={() => { setActiveCourse(null); setShowCertificateConfetti(false); }}
            //         className="absolute top-4 right-4 z-50 p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200 shadow-sm transition"
            //         title="Close Portal"
            //       >
            //         <X className="w-5 h-5" />
            //       </button>
            //       {/* LEFT COLUMN: Video Player (7 cols) */}
            //       <div className="bg-slate-900 col-span-7 sm:col-span-full bg-slate-950 flex flex-col justify-between p-0 text-white border-r border-slate-900 min-h-0 sm:min-h-[300px] relative select-none">
            //         {activeCourse.preview_video_url ? (
            //           <video
            //             // src="https://avtshare01.rz.tu-ilmenau.de/avt-vqdb-uhd-1/test_1/segments/bigbuck_bunny_8bit_15000kbps_1080p_60.0fps_h264.mp4"
            //             src={activeCourse.preview_video_url}
            //             controls
            //             autoPlay
            //             className="w-full h-full object-cover"
            //           />
            //         ) : (
            //           <div className="flex-1 flex flex-col items-center justify-center p-6">
            //             <PlayCircle className="w-16 h-16 text-slate-600 mb-4" />
            //             <p className="text-slate-400 text-sm">No preview video available</p>
            //           </div>
            //         )}
            //       </div>
            //       {/* RIGHT COLUMN: Chapter Checklist & Certificates (5 cols) */}
            //       <div className="bg-white col-span-5 sm:col-span-full p-6 flex flex-col justify-between sm:max-h-[400px] overflow-y-auto w-full">
            //         <div className="space-y-6">
            //           <div>
            //             <span className="text-[10px] bg-slate-50 border border-slate-200 text-slate-500 font-extrabold uppercase px-2.5 py-1 rounded-md">
            //               COURSE SYLLABUS
            //             </span>
            //             <h3 className="text-lg font-black text-slate-900 tracking-tight mt-3 leading-normal">
            //               {activeCourse.course_title}
            //             </h3>
            //             <p className="text-slate-500 text-xs font-medium mt-1 leading-normal">
            //               Check off finished lessons to advance your dynamic completion index.
            //             </p>
            //           </div>
            //           {/* Interactive syllabus list */}
            //           <div className="space-y-2.5 max-h-[35vh] sm:max-h-[25vh] overflow-y-auto pr-1 ">
            //             {activeCourse.syllabus && activeCourse.syllabus.length > 0 ? (
            //               activeCourse.syllabus.map((lesson, idx) => {
            //                 const isDone = !!userLessonsState[lesson.id];
            //                 return (
            //                   <div
            //                     key={lesson.id}
            //                     onClick={() => toggleLesson(lesson.id)}
            //                     className={`p-3.5 rounded-2xl border text-sm flex items-start gap-3 transition-all duration-300 cursor-pointer select-none ${isDone
            //                       ? 'bg-slate-50 border-emerald-200 text-slate-700 shadow-sm'
            //                       : 'bg-white border-slate-100 text-slate-800 hover:bg-slate-50/50 hover:border-slate-200'
            //                       }`}
            //                   >
            //                     <div className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center font-bold text-[10px] transition-all ${isDone
            //                       ? 'bg-emerald-500 border-emerald-500 text-white'
            //                       : 'border-slate-350 text-slate-400'
            //                       }`}>
            //                       {isDone ? '✓' : `${idx + 1}`}
            //                     </div>
            //                     <div className="flex-1 text-left min-w-0">
            //                       <div className={`font-semibold text-xs leading-snug truncate ${isDone ? 'text-slate-650 font-bold line-through' : 'text-slate-800'}`}>
            //                         {lesson.title}
            //                       </div>
            //                       <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 mt-1 block">
            //                         Duration: {lesson.duration}
            //                       </div>
            //                     </div>
            //                   </div>
            //                 );
            //               })
            //             ) : (
            //               <div className="p-4 text-center text-slate-400 text-sm">
            //                 No syllabus available for this course
            //               </div>
            //             )}
            //           </div>
            //         </div>
            //         {/* Bottom Certificate Portal Section */}
            //         <div className="mt-6 pt-5 border-t border-slate-150 space-y-4">
            //           {showCertificateConfetti ? (
            //             /* Completed State: Release Certificate CTA */
            //             <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl text-center space-y-3 animate-in zoom-in-95 duration-300">
            //               <div className="inline-flex p-2 bg-emerald-100 rounded-full text-emerald-600 animate-bounce">
            //                 <Award className="w-7 h-7" />
            //               </div>
            //               <div className="text-left">
            //                 <h4 className="text-sm font-black text-slate-900 tracking-tight text-center">Course Complete!</h4>
            //                 <p className="text-[11px] text-slate-500 font-semibold leading-relaxed text-center mt-1">
            //                   Congratulations! You have completed all course curriculum modules. Your professional certificate is live.
            //                 </p>
            //               </div>
            //               <button
            //                 onClick={() => triggerCertificatePdf(activeCourse.title)}
            //                 className="w-full py-3 bg-slate-900 hover:bg-slate-700 text-white font-extrabold rounded-xl text-xs shadow-lg shadow-emerald-500/10 active:scale-95 transition flex items-center justify-center gap-2"
            //               >
            //                 <GraduationCap className="w-4 h-4 text-emerald-400 animate-pulse" />
            //                 Acquire Talent Badge
            //               </button>
            //             </div>
            //           ) : (
            //             /* Standard Incomplete progress indicator */
            //             <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-2xl">
            //               <div className="text-left">
            //                 <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">STATUS</div>
            //                 <span className="text-xs font-black text-slate-800 tracking-tight block mt-0.5">
            //                   {getCourseProgress(activeCourse).completedCount === 0 ? 'Not Enrolled' : 'Active Progress'}
            //                 </span>
            //               </div>
            //               <div className="text-right">
            //                 <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">COMPLETION</div>
            //                 <span className="text-xs font-black text-brand-600 tracking-tight block mt-0.5">
            //                   {getCourseProgress(activeCourse).percentage}%
            //                 </span>
            //               </div>
            //             </div>
            //           )}
            //         </div>
            //       </div>
            //     </div>
            //   </div>
            // )
          }
        </div>
      </div>
    </>
  );
}
